// Careers content: the Careers page (sales & marketing's copy, built on the
// live xeratec.com/careers/ page) and the job-posting pages.
import {
  BadgeDollarSign,
  CalendarHeart,
  Clock,
  Flame,
  Footprints,
  HeartPulse,
  PiggyBank,
  Plane,
  Target,
} from "lucide-react";
import { jobPostings } from "../content/written";

/**
 * Where the application form on each job posting sends applications: a
 * public endpoint on PIT that emails them, with the attached files, to HR.
 * Leave it empty and the form tells applicants to email their resume.
 */
export const applicationEndpoint = "https://pit.xeratec.com/api/careers/apply";

/** The files the application form asks for. */
export const applicationFiles = [
  { name: "resume", label: "Resume", required: true },
  { name: "cover_letter", label: "Cover Letter", required: false },
];
export const applicationFileTypes = [".pdf", ".doc", ".docx"];
// Two files at this size stay under PIT's 10 MB request limit.
export const applicationMaxMb = 4;

/**
 * Where applicants are told to email their resume when the form can't send
 * (the same inbox PIT delivers applications to).
 */
export const careersEmail = "careers@xeratec.com";

/** mailto: link to the careers inbox with a prefilled subject. */
export const careersMailto = (subject) =>
  `mailto:${careersEmail}?subject=${encodeURIComponent(subject)}`;

/**
 * OPEN POSITIONS come from the job files in content/jobs/ (one Markdown file
 * per job; see content/README.md). A file with `status: open` shows on the
 * Careers page; `status: closed` keeps its page online with a "no longer
 * open" notice so old links never dead-end.
 */
export const openPositions = jobPostings
  .filter((job) => job.status === "open")
  .map(({ title, path, type, location, compensation, summary, ...job }) => ({
    title,
    path,
    type,
    location,
    compensation,
    summary,
    // `companyBenefits: false` hides the full-time benefits list on the
    // posting (e.g. part-time roles with their own, smaller package).
    companyBenefits: job.companyBenefits !== false,
  }));

/** Posting pages that are no longer open (closed files and the old site's). */
export const pastPostingPaths = [
  ...jobPostings.filter((job) => job.status !== "open").map((job) => job.path),
  "/full-stack-developer-mid-senior-level/",
];

/** The open position posted at `path`, if any. */
export const openPositionAt = (path) =>
  openPositions.find((position) => position.path === path);

/** Whether `path` is a job-posting page (open now, or posted before). */
export const isJobPosting = (path) =>
  Boolean(openPositionAt(path)) || pastPostingPaths.includes(path);

export const careersPage = {
  eyebrow: "Careers at Xeratec · Hancock, Michigan",
  title: "Do work that matters. *Live where you love.*",
  lede: "We build software that transforms how our customers operate — and we do it with true work-life balance, so you can make the most of where you live. If you're an accountable team player ready to join a growing team, we'd love to meet you.",
  heroImageAlt: "The Xeratec team together at the Hancock, Michigan office",
  values: {
    eyebrow: "Our core values",
    title: "How we show *up.*",
    text: "Six values we hire by, work by, and hold each other to. If these sound like you, keep scrolling.",
  },
  positions: {
    eyebrow: "Open positions",
    title: "Come join the *team.*",
    text: "Xeratec is looking for new teammates to fill the following roles. Read the full role and apply right on the posting.",
    none: "We don't have any openings posted right now — but we're always glad to meet accountable people who like solving hard problems.",
    noFit:
      "Don't see your role? We're always happy to meet talented people — send your resume to",
  },
  benefits: {
    eyebrow: "What you get",
    title: "Benefits we *offer.*",
    text: "Real benefits for real life — built on the same trust our core values demand.",
    perksTitle: "Office perks",
  },
  keweenaw: {
    eyebrow: "Where you'll live",
    title: "The purest of *Pure Michigan.*",
    text: [
      "In the northwest corner of Michigan's Upper Peninsula lies a hidden gem we're lucky enough to call home: the Keweenaw Peninsula. Surrounded by Lake Superior — the largest freshwater lake in the world — the Keweenaw boasts beautiful forests, sandy beaches, snowy winters, and plenty of great shops and restaurants.",
      "Outdoor recreation is everywhere, and Michigan Technological University — a top-rated computer science school — brings culture, diversity, and a wealth of resources to the area. Folks are friendly and always willing to lend a hand. It's a truly special place to live, and we can't wait for you to try life up here.",
    ],
    link: "Explore the Keweenaw",
    url: "https://www.michigan.org/keweenaw-peninsula",
  },
  cta: {
    eyebrow: "Sound like your kind of shop?",
    title: "Come build with *us.*",
    text: "Apply for an open role, or just introduce yourself — we're always glad to meet accountable people who like solving hard problems.",
  },
};

/** Keweenaw photos from the live careers page (original asset filenames). */
export const keweenawPhotos = [
  {
    file: "Houghton_2019_Aerial-scaled.jpg",
    alt: "Aerial view of Houghton and Hancock on either side of the Portage waterway",
    caption: "Houghton & Hancock",
  },
  {
    file: "image.jpeg",
    alt: "Two kayakers paddling past a shoreline of fall colors",
    caption: "Fall paddling",
  },
  {
    file: "LoveWinterPortageLake.jpg",
    alt: "People out on frozen Portage Lake in front of the lift bridge",
    caption: "Winter on Portage Lake",
  },
  {
    file: "Keweenaw-Peninsula-Sunset_@state_of_mind_photos.jpg",
    alt: "A hiker and dog on a rocky Lake Superior shore at sunset",
    caption: "Lake Superior sunsets",
  },
  {
    file: "1fb1599f2bfd138bc58bd9813238cdd6.jpg",
    alt: "The Portage Lake Lift Bridge framed by fall foliage",
    caption: "Lift bridge in the fall",
  },
  {
    file: "three-snowmobiles-riding-through-the-woods.jpg",
    alt: "Three snowmobiles riding through snowy woods",
    caption: "Snowmobile trails",
  },
];

export const coreValues = [
  {
    title: "Ownership",
    text: "We ensure our own personal investment in the areas and tasks we're responsible for.",
    quote: "“The buck stops with me.”",
  },
  {
    title: "Figureoutable",
    text: "We have the determined mindset to tackle any problem.",
  },
  {
    title: "Integrity",
    text: "We maintain an ethical relationship with both customers and coworkers.",
  },
  {
    title: "Humbled Confidence",
    text: "Calm confidence in our experience and abilities — with the humility to admit the things we don't know.",
  },
  {
    title: "Respectful Team Players",
    text: "We're considerate of our teammates' boundaries, work methods, and ideas. We have their backs, and we trust they have ours.",
  },
  {
    title: "Customer Stewards",
    text: "We respect our customers' money, time, resources, and opinions — using our expertise to deliver results while guiding them in the right direction.",
  },
];

export const benefits = [
  {
    title: "Competitive Wages",
    icon: BadgeDollarSign,
    text: "Based on your knowledge and experience, along with the current market.",
  },
  {
    title: "Health / Vision / Dental",
    icon: HeartPulse,
    text: "Insurance coverage offered to all employees.",
  },
  {
    title: "401(k) with 4% Match",
    icon: PiggyBank,
    text: "A competitive retirement plan with a generous employer match.",
  },
  {
    title: "Paid Time Off",
    icon: Plane,
    text: "Vacation and sick time accrue all year and roll over if unused (hours cap eventually).",
  },
  {
    title: "Flexible Work Environment",
    icon: Clock,
    text: "Come and go as you need. Our core values mean we trust our people with their time.",
  },
  {
    title: "7 Paid Holidays",
    icon: CalendarHeart,
    text: "Holiday PTO every year, on top of your accrued time.",
  },
];

export const perks = [
  {
    title: "Pool Table & Ping Pong",
    icon: Target,
    text: "Right in the Hancock headquarters — a great way to clear your mind and refocus during the day.",
  },
  {
    title: "Payday Grill-Outs",
    icon: Flame,
    text: "Pay-day Fridays mean BYOM (bring your own meat) and potluck lunches, weather permitting, late spring through fall.",
  },
  {
    title: "Walkability",
    icon: Footprints,
    text: "Restaurants and shops on your lunch break — and right across the street, a park with steps down to a walking path along the water.",
  },
];
